import classNames from 'classnames'
import Tooltip from 'rc-tooltip'
import { memo, useCallback } from 'react'
import { MdKeyboardArrowRight } from 'react-icons/md'
import { useMatch, NavLink, useLocation } from 'react-router-dom'

import type { INavBarLink } from '../types'
import type { ReactElement } from 'react'

import SubMenu from '../SubMenu'
import SubMenuPortal from '../SubMenuPortal'
import { subMenuMatch } from '../utils'

import './styles.scss'

export interface Props extends INavBarLink {
  authorizations: string[]
  hoveredItems: string[]
  isCollapsed: boolean
  level: number
  onHover: (value: string, level: number) => void
}

const NavLinkItem = ({
  authorizations,
  hoveredItems,
  icon,
  isCollapsed,
  isNewFeature = false,
  level,
  name,
  onHover,
  secondaryUrl,
  submenu,
  url,
}: Props): ReactElement => {
  /** Local state */
  const location = useLocation()

  const match = useMatch(url)

  const isNavLinkActive = useCallback(
    (isActive: boolean) =>
      isActive ||
      !!match ||
      (submenu && subMenuMatch(submenu)) ||
      (url !== '/' && location.pathname.includes(url)) ||
      (secondaryUrl && url !== '/' && location.pathname.includes(secondaryUrl)),
    [match, submenu, url, location.pathname, secondaryUrl]
  )

  /** Handlers */

  const handleMouseEnter = () => onHover(name, level)

  const handleMouseLeave = () => onHover('', level)

  return (
    <div className="nav-link-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Tooltip
        align={{
          offset: [20, -8],
        }}
        overlay={
          submenu ? (
            <SubMenuPortal
              authorizations={authorizations}
              hoveredItems={hoveredItems}
              isCollapsed={isCollapsed}
              level={1}
              onHover={onHover}
              submenu={submenu}
              title={name}
            />
          ) : isCollapsed ? (
            <SubMenuPortal
              authorizations={authorizations}
              hoveredItems={hoveredItems}
              isCollapsed={isCollapsed}
              level={1}
              onHover={onHover}
              title={name}
            />
          ) : null
        }
        placement="rightTop"
      >
        <NavLink
          className={({ isActive }) =>
            classNames('nav-link-simple-item', {
              active: isNavLinkActive(isActive),
              collapsed: isCollapsed,
              hovered: hoveredItems[level] === name,
            })
          }
          end
          to={url}
        >
          {icon ? icon : null}
          {!isCollapsed && (
            <div className="nav-link-title">
              <span className="nav-link-title__text">
                {name}
                {isNewFeature ? <span className="new-feature-icon">New</span> : null}
              </span>
              {submenu ? <MdKeyboardArrowRight className="nav-link-title__arrow" color="#fff" size={14} /> : null}
            </div>
          )}
        </NavLink>
      </Tooltip>
      {submenu ? (
        <SubMenu
          authorizations={authorizations}
          hoveredItems={hoveredItems}
          isCollapsed={isCollapsed}
          level={1}
          onHover={onHover}
          parentUrl={url}
          submenu={submenu}
        />
      ) : null}
    </div>
  )
}

export default memo(NavLinkItem)
